import { useState, useEffect, useRef, useCallback } from "react";
import useConditionalEffect from "../hooks/useConditionalEffect";
import DrawToolSecondMode from "./DrawTool/DrawToolSecondMode";
import { clearAllHandlers, clearLayers, registerDrawHandler } from "../utils/handlersUtils";
import { updateLineFromExistingPolygon } from "../utils/lineUtils";
import { addBuffer, clearBufferLayers, removeBuffersForPolyId, syncBuffersFor } from "../utils/bufferUtils";
import { addNearestRiskLinesToLotPerimeter, buildRiskDriverMeasurements, purgeMeasurementLayers } from "../utils/measurementUtils";
import { clearAssetPaintOverlay, ensureAssetPaintOverlay, refreshAssetPaintOverlay, tagAndPaintAssetRisk } from "../utils/assetRiskUtils";
import { detectCountryForFeature } from "../utils/countryUtils";
import { calculateMaxAssetRisk } from "../utils/calculateUtils";
import { debounce } from "../utils/debounce";

export const DEFAULT_BUFFERS = [1.5, 9, 30];
export const COUNTRY_PRESETS = {
  US: [1.5, 10, 30],
  GR: [2, 5, 10],
  FR: [10, 50],
};

const Satellite = ({
  drawModeRef,
  drawMode,
  setDrawMode,
  whichMode,
  setWichMode,
  isFirstMode,
  handleOpenFireFenceParams,
  handleInitialAsessmentParams,
  setFireFenceParams,
  setInitialAsessmentParams,
  setAnalysedParams,
}) => {
  const [activePolyId, setActivePolyId] = useState(null);
  const [bufferDistances, setBufferDistances] = useState(DEFAULT_BUFFERS);
  const [polygon, setPolygon] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [country, setCountry] = useState("US");
  const [rdCount, setRdCount] = useState(1);
  const [selectedRiskId, setSelectedRiskId] = useState(null);
  const [selectedRiskLabel, setSelectedRiskLabel] = useState("");

  const drawPolyLayerIdsRef = useRef([]);
  useConditionalEffect(
    () =>
      registerDrawHandler(
        window.map,
        window.draw,
        setFireFenceParams,
        setInitialAsessmentParams,
        setAnalysedParams,
        setDrawMode,
        whichMode,
        bufferDistances,
        setPolygon,
        setExpanded
      ),
    [drawMode[whichMode], whichMode, bufferDistances]
  );

  const buildingKeyToDrawIdRef = useRef(new Map());
  const drawIdToBuildingKeyRef = useRef(new Map());

  const getBuildingKey = (feature) => {
    const pid = feature.id ?? feature.properties?.id ?? feature.properties?.osm_id ?? feature.properties?.["@id"];
    if (pid != null) return `id:${pid}`;

    const geom = feature.geometry;
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    const scan = (coords) => {
      for (const c of coords) {
        if (Array.isArray(c[0])) {
          scan(c);
        } else {
          const [x, y] = c;
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    };

    scan(geom.coordinates);
    const r = (n) => n.toFixed(5);
    return `bbox:${r(minX)},${r(minY)},${r(maxX)},${r(maxY)}`;
  };

  const getActivePolyVertexCount = () => {
    const p = window.draw?.get(activePolyId);
    const ring = p?.geometry?.type === "Polygon" ? p.geometry.coordinates?.[0] || [] : [];
    return Math.max(0, ring.length - 1);
  };

  const handleContextualDelete = () => {
    const map = window.map;
    const draw = window.draw;
    if (!map || !draw) return;

    const mode = draw.getMode();

    if (mode === "direct_select") {
      const pts = draw.getSelectedPoints?.();
      const hasVertex = !!(pts && pts.features && pts.features.length);

      if (hasVertex) {
        const selectedFeatures = draw.getSelected?.();
        const selectedPoly = selectedFeatures?.features?.[0];

        if (selectedPoly?.properties?.lotPerimeter) {
          const vertexCount = getLotPerimeterVertexCount(selectedPoly);
          if (vertexCount > 3) {
            draw.trash();

            setTimeout(() => {
              const updatedPoly = draw.get(selectedPoly.id);
              if (updatedPoly) {
                updateLineFromExistingPolygon(map, updatedPoly, setFireFenceParams, setInitialAsessmentParams, setAnalysedParams);
              }
            }, 100);
          }
          return;
        }

        if (getActivePolyVertexCount() > 3) {
          draw.trash();
        }
        return;
      } else {
        const selectedFeatures = draw.getSelected?.();
        const selectedPoly = selectedFeatures?.features?.[0];
        if (selectedPoly?.properties?.lotPerimeter) {
          if (map.getLayer("line")) map.removeLayer("line");
          if (map.getLayer("line-outline")) map.removeLayer("line-outline");
          if (map.getSource("line")) map.removeSource("line");
          try {
            const mode = draw.getMode?.();
            if (mode?.name === "direct_select" || mode?.mode === "direct_select") {
              draw.changeMode("simple_select");
            }
          } catch { }
          draw.delete(selectedPoly.id);
          setActivePolyId(null);
          setPolygon(null);
        }
      }
    }

    if (mode === "simple_select" && activePolyId) {
      const poly = draw.get(activePolyId);

      if (poly?.properties?.isBuilding || poly?.properties?.assetContour) {
        if (map.getSource("nearest-asset-lot-line")) {
          map.removeLayer("nearest-asset-lot-line");
          map.removeLayer("nearest-asset-lot-line-label");
          map.removeSource("nearest-asset-lot-line");
        }
        removeBuffersForPolyId(map, activePolyId);
        clearAssetPaintOverlay(window.map);
        ensureAssetPaintOverlay(window.map);
      }

      // if (poly?.properties?.lotPerimeter) {
      //   if (map.getLayer('line')) map.removeLayer('line');
      //   if (map.getLayer('line-outline')) map.removeLayer('line-outline');
      //   if (map.getSource('line')) map.removeSource('line');
      // }

      draw.delete(activePolyId);
      setActivePolyId(null);
      setPolygon(null);
      setExpanded(false);

      refreshMeasurementsNow();
      return;
    }

    draw.trash();
  };

  const getLotPerimeterVertexCount = (polygon) => {
    if (!polygon || polygon.geometry?.type !== "Polygon") return 0;

    const ring = polygon.geometry.coordinates?.[0] || [];
    return Math.max(0, ring.length - 1);
  };

  const refreshRiskDriverLabels = () => {
    const map = window.map;
    const draw = window.draw;
    if (!map || !draw) return;

    const pts = draw.getAll().features.filter((f) => f?.geometry?.type === "Point");
    const fc = {
      type: "FeatureCollection",
      features: pts.map((f) => ({
        type: "Feature",
        geometry: f.geometry,
        properties: { label: f?.properties?.label ?? "" },
      })),
    };

    if (!pts.length) {
      if (map.getLayer("risk-driver-labels")) map.removeLayer("risk-driver-labels");
      if (map.getSource("risk-driver-points")) map.removeSource("risk-driver-points");
      return;
    }

    if (map.getSource("risk-driver-points")) {
      map.getSource("risk-driver-points").setData(fc);
    } else {
      map.addSource("risk-driver-points", { type: "geojson", data: fc });
      map.addLayer({
        id: "risk-driver-labels",
        type: "symbol",
        source: "risk-driver-points",
        layout: {
          "text-field": ["get", "label"],
          "text-size": 12,
          "text-anchor": "top",
          "text-offset": [0, 1],
        },
        paint: { "text-halo-color": "#fff", "text-halo-width": 1 },
      });
    }
  };

  const purgeRiskDriverLabels = () => {
    const map = window.map;
    if (!map) return;
    if (map.getLayer("risk-driver-labels")) map.removeLayer("risk-driver-labels");
    if (map.getSource("risk-driver-points")) map.removeSource("risk-driver-points");
  };

  const refreshMeasurementsNow = () => {
    const map = window.map;
    const draw = window.draw;
    if (!map || !draw) return;

    const fc = buildRiskDriverMeasurements(map, draw);

    if (!fc.features.length) {
      purgeMeasurementLayers(map);
      return;
    }

    if (map.getSource("risk-lines")) {
      map.getSource("risk-lines").setData(fc);
    } else {
      map.addSource("risk-lines", { type: "geojson", data: fc });

      map.addLayer({
        id: "risk-lines",
        type: "line",
        source: "risk-lines",
        paint: {
          "line-color": "#ff0000",
          "line-width": 2,
          "line-dasharray": [2, 2],
        },
      });

      map.addLayer({
        id: "risk-lines-label",
        type: "symbol",
        source: "risk-lines",
        layout: {
          "symbol-placement": "line",
          "text-field": ["format", ["get", "distance"], { "font-scale": 1 }, " m"],
          "text-size": 12,
          "text-offset": [0, -0.5],
        },
        paint: { "text-halo-color": "#fff", "text-halo-width": 1 },
      });
    }
  };

  const applyPreset = (c) => {
    const base = COUNTRY_PRESETS[c] || COUNTRY_PRESETS.US;
    setCountry(c);
    const map = window.map;
    const draw = window.draw;
    if (!map || !draw) return;
    if (!activePolyId) {
      setBufferDistances([...base]);
      return;
    }
    const poly = draw.get(activePolyId);
    if (!poly) return;
    draw.setFeatureProperty(poly.id, "country", c);
    draw.setFeatureProperty(poly.id, "buffers", [...base]);
    setBufferDistances([...base]);
    syncBuffersFor(poly, map, base);
    refreshMeasurementsNow();
  };

  useEffect(() => {
    const map = window.map;
    if (!map) return;
    const update = () => {
      const layers = map.getStyle()?.layers || [];
      drawPolyLayerIdsRef.current = layers.map((l) => l.id).filter((id) => id && id.indexOf("gl-draw-polygon") === 0);
    };
    update();

    const handleAddNearestRiskLinesToLotPerimeter = (e) => {
      addNearestRiskLinesToLotPerimeter(map, e);
    }

    const handleCalculateMaxRisk = debounce(() => {
      const maxAssetRisk = calculateMaxAssetRisk();

      setInitialAsessmentParams((prev) => ({
        ...prev,
        maxAssetRisk
      }));
    }, 200);

    map.on("styledata", update);
    map.on("draw.update", handleAddNearestRiskLinesToLotPerimeter)
    map.on("draw.selectionchange", handleAddNearestRiskLinesToLotPerimeter);
    map.on("draw.render", handleCalculateMaxRisk);

    return () => {
      map.off("styledata", update);
      map.off("draw.selectionchange", handleAddNearestRiskLinesToLotPerimeter);
      map.off("draw.update", handleAddNearestRiskLinesToLotPerimeter);
      map.off("draw.render", handleCalculateMaxRisk);
    };
  }, [setInitialAsessmentParams]);

  const clickHitsDrawPolygon = (e) => {
    const map = window.map;
    const ids = drawPolyLayerIdsRef.current;
    if (!map || !ids.length) return false;
    const hits = map.queryRenderedFeatures(e.point, { layers: ids });
    return hits && hits.length > 0;
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;

      const tag = (document.activeElement && document.activeElement.tagName) || "";
      const typing = /INPUT|TEXTAREA|SELECT/.test(tag) || document.activeElement?.isContentEditable;
      if (typing) return;

      e.preventDefault();
      handleContextualDelete();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activePolyId]);

  const assetContourActive = drawMode.home === "draw_polygon";

  useEffect(() => {
    const map = window.map;
    if (!map || isFirstMode) return;

    const handleBuildingClick = (e) => {
      if (clickHitsDrawPolygon(e) || !assetContourActive) return;

      const map = window.map;

      const draw = window.draw;

      const building = e.features?.[0];
      if (!building?.geometry) return;

      const key = getBuildingKey(building);
      const existingId = buildingKeyToDrawIdRef.current.get(key);
      const existing = draw.get(existingId);
      if (draw && existing) return;

      if (buildingKeyToDrawIdRef.current.has(key)) {
        if (existing) {
          draw.changeMode("direct_select", { featureId: existingId });
          setActivePolyId(existingId);
          setPolygon(existing);
          const b = existing.properties?.buffers || DEFAULT_BUFFERS;
          setBufferDistances([...b]);
          const c = existing.properties?.country ?? detectCountryForFeature(existing);
          setCountry(c);
          setExpanded(true);
          setWichMode("home");
          setDrawMode((prev) => ({ ...prev, home: "simple_select", area: "simple_select", point: "simple_select" }));
          return;
        } else {
          buildingKeyToDrawIdRef.current.delete(key);
        }
      }

      const detectedCountry = detectCountryForFeature(building);
      const preset = COUNTRY_PRESETS[detectedCountry] || DEFAULT_BUFFERS;

      const poly = {
        type: "Feature",
        geometry: building.geometry,
        properties: {
          buffers: [...preset],
          country: detectedCountry,
          isBuilding: true,
        },
      };

      const newId = draw.add(poly);
      try {
        tagAndPaintAssetRisk(map, draw, draw.get(newId));
      } catch { }
      buildingKeyToDrawIdRef.current.set(key, newId);
      drawIdToBuildingKeyRef.current.set(newId, key);

      map.once("draw.render", () => {
        draw.changeMode("direct_select", { featureId: newId });
      });

      const created = draw.get(newId);
      setPolygon(created);

      bufferDistances.forEach((d, i) => addBuffer(created, d, i, map, newId));

      setWichMode("home");
      setDrawMode((prev) => ({ ...prev, home: "simple_select", area: "simple_select", point: "simple_select" }));
      setExpanded(true);
    };

    const onDrawDeleteCleanup = (e) => {
      e.features?.forEach((f) => {
        if (!f?.id) return;
        const id = f.id;
        const key = drawIdToBuildingKeyRef.current.get(id);
        if (key) {
          buildingKeyToDrawIdRef.current.delete(key);
          drawIdToBuildingKeyRef.current.delete(id);
        }
      });
    };
    const enter = () => {
      if (assetContourActive) map.getCanvas().style.cursor = "pointer";
    };
    const leave = () => {
      map.getCanvas().style.cursor = "";
    };

    let buildingsClickEnabled = true;
    const enableBuildingsClick = () => {
      if (!buildingsClickEnabled) {
        map.on("click", "3d-buildings", handleBuildingClick);
        buildingsClickEnabled = true;
      }
    };
    const disableBuildingsClick = () => {
      if (buildingsClickEnabled) {
        map.off("click", "3d-buildings", handleBuildingClick);
        buildingsClickEnabled = false;
      }
    };

    const onModeChange = (ev) => {
      const m = ev?.mode || "";
      if (m === "direct_select" || m.startsWith?.("draw_")) {
        disableBuildingsClick();
      } else {
        enableBuildingsClick();
      }
    };
    map.on("draw.modechange", onModeChange);

    if (assetContourActive && map.getLayer("3d-buildings")) {
      map.on("click", "3d-buildings", handleBuildingClick);
      map.on("mouseenter", "3d-buildings", enter);
      map.on("mouseleave", "3d-buildings", leave);
    }
    map.on("draw.delete", onDrawDeleteCleanup);
    return () => {
      if (map.getLayer("3d-buildings")) {
        map.off("click", "3d-buildings", handleBuildingClick);
        map.off("mouseenter", "3d-buildings", enter);
        map.off("mouseleave", "3d-buildings", leave);
      }
      map.off("draw.delete", onDrawDeleteCleanup);
      map.getCanvas().style.cursor = "";
    };
  }, [isFirstMode, bufferDistances, assetContourActive, setDrawMode, setWichMode]);

  useEffect(() => {
    const map = window.map;
    const draw = window.draw;
    if (!map || !draw) return;

    const onSelect = (e) => {
      const poly = e.features?.[0];

      if (poly?.geometry?.type === "Point") {
        setSelectedRiskId(poly.id);
        setSelectedRiskLabel(poly?.properties?.label ?? "");
        return;
      } else {
        setSelectedRiskId(null);
        setSelectedRiskLabel("");
      }

      if (!poly) {
        setActivePolyId(null);
        setExpanded(false);
        return;
      }

      setActivePolyId(poly.id);

      if (poly.properties?.isBuilding || poly.properties?.assetContour) {
        const b = poly.properties?.buffers || DEFAULT_BUFFERS;
        setBufferDistances([...b]);
        const polyCountry = poly.properties?.country ?? detectCountryForFeature(poly);
        setCountry(polyCountry);
        setExpanded(true);
      } else {
        setExpanded(false);
      }
    };

    map.on("draw.selectionchange", onSelect);
    return () => map.off("draw.selectionchange", onSelect);
  }, [polygon]);

  useEffect(() => {
    if (!activePolyId) return;

    const map = window.map;
    const poly = window.draw.get(activePolyId);
    if (!poly) return;

    if (!poly.properties?.isBuilding && !poly.properties?.assetContour) return;

    poly.properties.buffers = [...bufferDistances];
    window.draw.setFeatureProperty(activePolyId, "buffers", poly.properties.buffers);

    syncBuffersFor(poly, map, bufferDistances);
    refreshMeasurementsNow();
  }, [bufferDistances, activePolyId]);

  const handlePlus = (index) => {
    const newBufferDistances = [...bufferDistances];
    newBufferDistances[index] += 1;
    setBufferDistances(newBufferDistances);
  };
  const handleMinus = (index) => {
    const newBufferDistances = [...bufferDistances];
    newBufferDistances[index] -= 1;
    setBufferDistances(newBufferDistances);
  };

  const handleExpanded = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  const toggleDrawMode = () => {
    setWichMode("area");
    setDrawMode((prev) => ({
      ...prev,
      area: prev.area === "simple_select" ? "draw_polygon" : "simple_select",
      home: "simple_select",
      point: "simple_select",
    }));
    if (drawModeRef.current && drawMode.area === "simple_select") {
      drawModeRef.current("simple_select");
      drawModeRef.current("draw_polygon");
    } else {
      drawModeRef.current("simple_select");
    }
  };

  const toggleDrawModeHome = () => {
    setWichMode("home");
    setDrawMode((prev) => ({
      ...prev,
      home: prev.home === "simple_select" ? "draw_polygon" : "simple_select",
      area: "simple_select",
      point: "simple_select",
    }));
    if (drawModeRef.current && drawMode.home === "simple_select") {
      drawModeRef.current("simple_select");
      drawModeRef.current("draw_polygon");
    } else {
      drawModeRef.current("simple_select");
    }
  };
  const toggleDrawModePoint = () => {
    setWichMode("point");
    setDrawMode((prev) => ({
      ...prev,
      point: prev.point === "simple_select" ? "draw_point" : "simple_select",
      area: "simple_select",
      home: "simple_select",
    }));
    if (drawModeRef.current && drawMode.point === "simple_select") {
      drawModeRef.current("simple_select");
      drawModeRef.current("draw_point");
    } else {
      drawModeRef.current("simple_select");
    }
  };

  useEffect(() => {
    const map = window.map;
    const draw = window.draw;
    if (!map || !draw) return;

    const onCreate = (e) => {
      let touched = false;
      e.features?.forEach((f) => {
        if (f?.geometry?.type === "Point") {
          const cur = f?.properties?.label;
          if (cur == null || cur === "") {
            const next = String(rdCount);
            draw.setFeatureProperty(f.id, "label", next);
            setRdCount((c) => c + 1);
            touched = true;
          }
        }

        if (f?.geometry?.type === "Polygon" && whichMode === "home" && !f.properties?.lotPerimeter) {
          const id = f.id;
          const c = detectCountryForFeature(f);
          const preset = COUNTRY_PRESETS[c] || DEFAULT_BUFFERS;

          draw.setFeatureProperty(id, "country", c);
          draw.setFeatureProperty(id, "buffers", [...preset]);
          draw.setFeatureProperty(id, "assetContour", true);

          setActivePolyId(id);
          setCountry(c);
          setBufferDistances([...preset]);
          try {
            tagAndPaintAssetRisk(map, draw, draw.get(id));
          } catch { }
          const poly = draw.get(id);
          if (poly) syncBuffersFor(poly, map, preset);

          map.once("draw.render", () => draw.changeMode("direct_select", { featureId: id }));
          setExpanded(true);
        }
      });

      if (touched) refreshRiskDriverLabels();
      refreshMeasurementsNow();
    };

    const onUpdate = () => {
      if (activePolyId) {
        try {
          tagAndPaintAssetRisk(map, draw, draw.get(activePolyId));
        } catch { }
      }
      refreshRiskDriverLabels();
      refreshMeasurementsNow();
    };

    const onDelete = (e) => {
      const deletedLotPerimeter = e.features?.find((f) => f.properties?.lotPerimeter);
      if (deletedLotPerimeter) {
        if (map.getLayer("line")) map.removeLayer("line");
        if (map.getLayer("line-outline")) map.removeLayer("line-outline");
        if (map.getSource("line")) map.removeSource("line");
      }

      refreshRiskDriverLabels();
      refreshMeasurementsNow();
    };

    map.on("draw.create", onCreate);
    map.on("draw.update", onUpdate);
    map.on("draw.delete", onDelete);

    return () => {
      map.off("draw.create", onCreate);
      map.off("draw.update", onUpdate);
      map.off("draw.delete", onDelete);
    };
  }, [rdCount, polygon, bufferDistances, whichMode, activePolyId]);

  const clearHandler = useCallback(() => {
    const map = window.map;
    const draw = window.draw;

    if (map.getLayer("line")) map.removeLayer("line");
    if (map.getLayer("line-outline")) map.removeLayer("line-outline");
    if (map.getSource("line")) map.removeSource("line");
    if (map.getLayer("nearest-asset-lot-line")) {
      map.removeLayer("nearest-asset-lot-line");
      map.removeLayer("nearest-asset-lot-line-label");
      map.removeSource("nearest-asset-lot-line");
    }

    clearAllHandlers(map);
    clearLayers();
    clearBufferLayers();

    setDrawMode((prev) => ({
      ...prev,
      area: "simple_select",
      home: "simple_select",
      point: "simple_select",
    }));
    setPolygon(null);
    setActivePolyId(null);
    setExpanded(false);
    setSelectedRiskId(null);
    setSelectedRiskLabel("");
    setWichMode("area");
    setFireFenceParams({ isOpen: false });
    setInitialAsessmentParams({ isOpen: false });

    purgeMeasurementLayers(map);
    purgeRiskDriverLabels();

    draw.changeMode("simple_select");
    draw.deleteAll();

    buildingKeyToDrawIdRef.current.clear();
    drawIdToBuildingKeyRef.current.clear();

    clearAssetPaintOverlay(window.map);
    ensureAssetPaintOverlay(window.map);
    refreshAssetPaintOverlay(window.map, window.draw);

    if (map.__lotRing) {
      map.__lotRing = null;
    }
  }, [setDrawMode, setFireFenceParams, setInitialAsessmentParams, setWichMode]);

  const drawModeNow = window.draw?.getMode?.() || "";
  const deleteDisabled = (drawModeNow === "direct_select" && getActivePolyVertexCount() <= 3) || (drawModeNow === "simple_select" && !activePolyId);
  return (
    <DrawToolSecondMode
      toggleDrawMode={toggleDrawMode}
      drawMode={drawMode}
      toggleDrawModePoint={toggleDrawModePoint}
      clearHandler={clearHandler}
      toggleDrawModeHome={toggleDrawModeHome}
      handleExpanded={handleExpanded}
      isExpanded={expanded}
      applyPreset={applyPreset}
      country={country}
      deleteDisabled={deleteDisabled}
      handleContextualDelete={handleContextualDelete}
      bufferDistances={bufferDistances}
      handlePlus={handlePlus}
      handleMinus={handleMinus}
      setBufferDistances={setBufferDistances}
      selectedRiskId={selectedRiskId}
      selectedRiskLabel={selectedRiskLabel}
      setSelectedRiskLabel={setSelectedRiskLabel}
      refreshMeasurementsNow={refreshMeasurementsNow}
      refreshRiskDriverLabels={refreshRiskDriverLabels}
      setSelectedRiskId={setSelectedRiskId}
      handleOpenFireFenceParams={handleOpenFireFenceParams}
      handleInitialAsessmentParams={handleInitialAsessmentParams}
    />
  );
};

export default Satellite;
